/**
 * ArgoSuggestsStrip — the proactive "Argo suggests" surface.
 *
 * Renders the top nudges from the PURE `proactiveNudges` engine as a small
 * strip on relevant views (CRM, Customer Success) and inside the Copilot
 * dock — the agent tapping you on the shoulder with a concrete next move,
 * everywhere you work. Each nudge carries a suggested GOVERNED action;
 * clicking it queues the action into the EXISTING approvals inbox
 * (`queueApproval` → Dashboard → `resolveApproval`), so accepting a
 * suggestion is proposed, audited, and reversible — never a silent write.
 *
 * Honest by construction: renders NOTHING when the engine finds nothing
 * (no filler "all good!" cards), and a queued nudge flips to a "queued —
 * approve in the Dashboard inbox" pill instead of pretending the work is
 * already done.
 */

import { useState } from 'react';
import { ArrowRight, Check, Loader2, Sparkles } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { DraftFirstControls, NeedsEmailPill } from './DraftFirstControls';
import { C } from '../lib/tokens';
import { queueApproval } from '../lib/agent/approvals';
import { draftForNudge } from '../lib/agent/draftFirst';
import { topNudges, type Nudge } from '../lib/agent/proactiveNudges';
import type { Person } from '../lib/db/types';

export interface ArgoSuggestsStripProps {
  nudges: Nudge[];
  /** How many nudges to surface (default 3; the dock passes 2). */
  limit?: number;
  /** False for viewers — suggestions render read-only, no queue buttons. */
  canMutate: boolean;
  /** Compact layout for the Copilot dock (tighter padding, stacked rows). */
  compact?: boolean;
  /**
   * Live people snapshot — enables draft-first cards: when a nudge's record
   * composes into a finished email (src/lib/agent/draftFirst.ts), the card
   * carries the artifact with [Approve & send] [Edit] [Skip] and the old
   * queue-task button is removed. Omitted → every card keeps the old
   * governed queue button (honest fallback, no fake drafts).
   */
  people?: Person[];
}

type QueueState = 'queuing' | 'queued' | 'failed';

export function ArgoSuggestsStrip({
  nudges,
  limit = 3,
  canMutate,
  compact = false,
  people,
}: ArgoSuggestsStripProps) {
  // Per-nudge queue lifecycle, keyed by the engine's stable nudge id.
  const [queueState, setQueueState] = useState<Record<string, QueueState>>({});
  // Draft-first [Skip]: dismissed suggestion ids — hidden, nothing queued.
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});

  const visible = topNudges(nudges, limit).filter((n) => !skipped[n.id]);
  // Nothing to suggest → no chrome at all. The agent stays quiet when the
  // book is clean instead of manufacturing a reassurance card.
  if (visible.length === 0) return null;

  const queue = async (nudge: Nudge) => {
    if (!canMutate || queueState[nudge.id] === 'queuing' || queueState[nudge.id] === 'queued') {
      return;
    }
    setQueueState((s) => ({ ...s, [nudge.id]: 'queuing' }));
    try {
      // The EXISTING governed lane: proposal → Dashboard inbox → approval.
      await queueApproval(nudge.action, 'autopilot', nudge.reason);
      setQueueState((s) => ({ ...s, [nudge.id]: 'queued' }));
    } catch {
      setQueueState((s) => ({ ...s, [nudge.id]: 'failed' }));
    }
  };

  return (
    <GlassPanel
      variant="subtle"
      animate={false}
      style={{ padding: compact ? '10px 12px' : '12px 16px' }}
      aria-label="Argo suggests"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span
          className="glass-pill glass-pill--magenta"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <Sparkles size={11} />
          Argo suggests
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          proactive · governed — every action needs your approval
        </span>
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {visible.map((nudge) => {
          const state = queueState[nudge.id];
          // Draft-first: when the record composes into a finished email, the
          // card carries the artifact itself — the old queue-task button is
          // removed. Null → honest fallback to the governed queue button.
          const draftState =
            people && canMutate ? draftForNudge(nudge, people) : null;
          return (
            <div
              key={nudge.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
                padding: compact ? '6px 8px' : '8px 10px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <span className="t-card-title" style={{ display: 'block', fontSize: 13 }}>
                  {nudge.headline} <span style={{ color: C.magentaSoft }}>{nudge.suggestion}</span>
                </span>
                <span
                  className="t-meta"
                  style={{ color: C.textDim, display: 'block', marginTop: 2 }}
                  title={nudge.reason}
                >
                  {nudge.reason}
                </span>
              </div>
              {draftState?.state === 'draft' && (
                <div style={{ flex: '1 1 100%', minWidth: 0 }}>
                  <DraftFirstControls
                    draft={draftState}
                    note={nudge.reason}
                    onSkip={() => setSkipped((s) => ({ ...s, [nudge.id]: true }))}
                  />
                </div>
              )}
              {draftState?.state === 'needs-email' && (
                <NeedsEmailPill personName={draftState.personName} />
              )}
              {draftState?.state !== 'draft' && canMutate && state !== 'queued' && (
                <button
                  type="button"
                  className="glass-btn"
                  disabled={state === 'queuing'}
                  onClick={() => void queue(nudge)}
                  title="Queues this governed action into the Dashboard approvals inbox — nothing runs until you approve it"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    border: '1px solid rgba(255,0,255,0.25)',
                    flex: '0 0 auto',
                  }}
                >
                  {state === 'queuing' ? (
                    <Loader2 size={12} style={{ animation: 'auth-spin 0.8s linear infinite' }} />
                  ) : (
                    <ArrowRight size={12} />
                  )}
                  {state === 'failed' ? 'Retry' : nudge.actionLabel}
                </button>
              )}
              {state === 'queued' && (
                <span
                  className="glass-pill glass-pill--green"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}
                >
                  <Check size={11} />
                  Queued — approve in the Dashboard inbox
                </span>
              )}
            </div>
          );
        })}
      </div>
    </GlassPanel>
  );
}
